import { useInfiniteQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { SearchIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserBase } from 'sdk';
import { appConfig } from 'shared';
import { useDebounce } from '~/hooks/use-debounce';
import { useFocusByRef } from '~/hooks/use-focus-by-ref';
import { useMountedState } from '~/hooks/use-mounted-state';
import { channelListQueriesByType } from '~/list-queries-config';
import { ComboboxSearchInput } from '~/modules/common/combobox-search-input';
import { ContentPlaceholder } from '~/modules/common/content-placeholder';
import { useDialoger } from '~/modules/common/dialoger/use-dialoger';
import { type HistoryEntry, SearchHistoryGroup } from '~/modules/common/search-history-group';
import type { EnrichedChannel } from '~/modules/entities/types';
import { SearchResultBlock } from '~/modules/navigation/menu-sheet/search-result-block';
import { useNavigationStore } from '~/modules/navigation/navigation-store';
import { Combobox, ComboboxEmpty, ComboboxList } from '~/modules/ui/combobox';
import { ScrollArea } from '~/modules/ui/scroll-area';
import { Skeleton } from '~/modules/ui/skeleton';
import { usersListQueryOptions } from '~/modules/user/query';
import { getChannelRoute, pageTopHashNav } from '~/utils/channel-route';
import { cn } from '~/utils/cn';
import { addRecentSearch, resolveSearchInput } from '~/utils/recent-searches';

const searchableEntityTypes = ['user', ...appConfig.channelEntityTypes] as const;

function SearchResultsSkeleton() {
  // Stays hidden briefly, so a fast response never flashes the skeleton.
  const { hasStarted } = useMountedState();

  return (
    <div className={cn('flex flex-col gap-4 p-4 transition-opacity duration-300', hasStarted ? 'opacity-100' : 'opacity-0')}>
      {Array.from({ length: 3 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholder.
        <div key={i} className="flex items-center gap-3 py-1.5">
          <Skeleton className="size-10 shrink-0 rounded-full" />
          <Skeleton className="h-4 w-48" />
        </div>
      ))}
    </div>
  );
}

type SearchSelection = EnrichedChannel | UserBase | HistoryEntry;

export function AppSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);

  const [searchValue, setSearchValue] = useState('');
  // Debounced so a request does not fire on every keystroke.
  const debouncedSearchValue = useDebounce(searchValue, 300, { immediateValue: '' });
  // Group collapse state lives here so it persists across reloads while typing; it resets with the dialog.
  const [collapsedTypes, setCollapsedTypes] = useState<Partial<Record<string, boolean>>>({});
  const toggleCollapsed = (entityType: string) => setCollapsedTypes((prev) => ({ ...prev, [entityType]: !prev[entityType] }));

  const recentSearches = useNavigationStore((state) => state.recentSearches);

  const { focusRef } = useFocusByRef();

  const deleteItemFromList = (item: string) => {
    useNavigationStore.setState((state) => {
      const searches = [...state.recentSearches];
      const index = searches.indexOf(item);
      if (index === -1) return;
      searches.splice(index, 1);
      return { ...state, recentSearches: searches };
    });
  };

  const updateRecentSearches = (value: string) => {
    useNavigationStore.setState((state) => {
      // Most recent on top, with normalized containment dedupe (shared with docs search).
      const searches = addRecentSearch(state.recentSearches, value);
      return searches === state.recentSearches ? state : { ...state, recentSearches: searches };
    });
  };

  const userQ = useInfiniteQuery({ ...usersListQueryOptions({ q: debouncedSearchValue }), enabled: debouncedSearchValue.length > 0 });

  const channelResults = Object.fromEntries(
    Object.entries(channelListQueriesByType).map(([entityType, queryOptions]) => [
      entityType,
      useInfiniteQuery({
        // biome-ignore lint/suspicious/noExplicitAny: queryOptions union covers heterogeneous entity types.
        ...(queryOptions as any)({ q: debouncedSearchValue }),
        enabled: debouncedSearchValue.length > 0,
      }),
    ]),
  );

  const users = debouncedSearchValue.length > 0 ? (userQ.data?.pages.flatMap((p) => p.items) ?? []) : [];
  const channelData = Object.fromEntries(
    Object.entries(channelResults).map(([entityType, query]) => [
      entityType,
      // biome-ignore lint/suspicious/noExplicitAny: query data types vary per entity
      debouncedSearchValue.length > 0 ? ((query.data as any)?.pages.flatMap((p: any) => p.items) ?? []) : [],
    ]),
  );

  const data: Record<string, (EnrichedChannel | UserBase)[]> = { user: users, ...channelData };
  const notFound = users.length === 0 && Object.values(channelData).every((items) => items.length === 0);
  // The debounce gap counts as loading, so the skeleton shows in place of the empty state.
  const isDebouncePending = searchValue.length > 0 && searchValue !== debouncedSearchValue;
  // The gap is included here too, keeping the input spinner visible while typing. Query activity only counts
  // while there is a query: an empty q shares its cache key with default list pages, whose refetches would
  // otherwise keep the spinner alive after the input is cleared.
  const hasQuery = debouncedSearchValue.length > 0;
  const isQueryFetching = hasQuery && (userQ.isFetching || Object.values(channelResults).some((q) => q.isFetching));
  const isFetching = isDebouncePending || isQueryFetching;
  const isLoading = searchValue.length > 0 && (isDebouncePending || userQ.isLoading || Object.values(channelResults).some((q) => q.isLoading));

  const onSelectItem = (item: EnrichedChannel | UserBase) => {
    updateRecentSearches(searchValue);

    if (item.entityType === 'user') {
      navigate({ to: '.', search: (prev) => ({ ...prev, userSheetId: item.id }), resetScroll: false });
    } else {
      const { to, params, search } = getChannelRoute(item);
      navigate({ to, params, search, ...pageTopHashNav, resetScroll: false });
    }

    useDialoger.getState().remove();
  };

  return (
    <Combobox<SearchSelection>
      inline
      openOnInputClick={false}
      value={null}
      onValueChange={(selection) => {
        if (!selection) return;
        if ('kind' in selection && selection.kind === 'history') {
          setSearchValue(selection.value);
          return;
        }
        onSelectItem(selection as EnrichedChannel | UserBase);
      }}
      inputValue={searchValue}
      onInputValueChange={(value) => setSearchValue(resolveSearchInput(searchValue, value, recentSearches))}
      filter={() => true}
    >
      <div className="rounded-lg shadow-2xl">
        <ComboboxSearchInput
          ref={focusRef}
          value={searchValue}
          isSearching={isFetching}
          spinnerDelay={0}
          className="h-12 text-lg"
          wrapClassName="h-12 text-lg"
          placeholder={t('c:placeholder.search')}
        />
        {/* Height and scrolling live on the ScrollArea viewport; the list's own max-h and overflow are cleared. */}
        <ScrollArea id={'item-search'} ref={scrollAreaRef} className="sm:h-[40vh]">
          <ComboboxList className="h-full max-h-none overflow-visible">
            {!isLoading && notFound && !(!searchValue.length && !!recentSearches.length) && (
              <ComboboxEmpty className="h-full sm:h-[36vh]">
                <ContentPlaceholder
                  icon={SearchIcon}
                  title={debouncedSearchValue.length ? 'c:no_resource_found' : 'c:global_search.text'}
                  titleProps={{ resource: t('c:results').toLowerCase(), appName: appConfig.name }}
                />
              </ComboboxEmpty>
            )}
            {notFound && !searchValue.length && !!recentSearches.length && (
              <SearchHistoryGroup searches={recentSearches} onRemove={deleteItemFromList} />
            )}
            {isLoading ? (
              <SearchResultsSkeleton />
            ) : (
              <div className="p-1">
                {(() => {
                  const firstWithResults = searchableEntityTypes.find((entityType) => (data[entityType] ?? []).length > 0);
                  return searchableEntityTypes.map((entityType) => (
                    <SearchResultBlock
                      key={entityType}
                      results={data[entityType] ?? []}
                      entityType={entityType}
                      hideSeparator={entityType === firstWithResults}
                      collapsed={!!collapsedTypes[entityType]}
                      onToggleCollapsed={() => toggleCollapsed(entityType)}
                    />
                  ));
                })()}
              </div>
            )}
          </ComboboxList>
        </ScrollArea>
      </div>
    </Combobox>
  );
}
